import React, { useState, useEffect } from "react";
import {
  Card,
  Box,
  CardContent,
  Typography,
  CardActions,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
} from "@mui/material";
import { API_URLS } from "../../config/apiUrls";
import { API_IBGE } from "../../config/apiIBGE";

interface User {
  id: string;
  financialRegister: string | null;
  name: string;
  email: string;
  phone: string;
  createdAt: string;
  ufId: number;
  cityId: string;
  city: {
    id: number;
    name: string;
  };
}

interface GerenciarUsuariosCardUserProps {
  user: User;
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
  onDelete: (userId: string) => void; // Callback para remoção
}

// Interface corrigida para incluir o id
interface Estado {
  id: number;
  sigla: string;
  nome: string;
  regiao: {
    id: number;
    sigla: string;
    nome: string;
  };
}

const GerenciarUsuariosCardUser: React.FC<GerenciarUsuariosCardUserProps> = ({
  user,
  token,
  role,
  onDelete,
}) => {
  const {
    id,
    financialRegister,
    name,
    email,
    phone,
    createdAt,
    ufId,
    cityId,
    city,
  } = user;

  // Estados para cidade e estado
  const [estado, setEstado] = useState<Estado | null>(null); // Estado completo
  const [cidade, setCidade] = useState<string>(""); // Nome da cidade

  // Estados para controle do Dialog e Snackbar
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );
  const [isDeleting, setIsDeleting] = useState<boolean>(false); // Controle de exclusão

  // Função para formatar a data de forma mais legível
  const formatDate = (date: string | null) => {
    if (!date) return "";
    const formattedDate = new Date(date).toLocaleString();
    return formattedDate;
  };

  const capitalizeWords = (str: string) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  // Função para abrir o dialog de confirmação
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  // Função para fechar o dialog sem excluir
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Função para fechar o Snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Função para excluir o usuário
  const handleDelete = async () => {
    setIsDeleting(true); // Indica que está excluindo
    try {
      const response = await fetch(`${API_URLS.deleteusers}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Falha ao excluir o usuário");
      }

      // Chama o onDelete para remover o usuário da interface
      onDelete(id);
      setSnackbarMessage("Usuário excluído com sucesso!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setOpenDialog(false); // Fecha o dialog após a exclusão
    } catch (error) {
      console.error("Erro ao excluir o usuário:", error);
      setSnackbarMessage("Erro ao excluir o usuário.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setIsDeleting(false); // Permite novas tentativas de exclusão
    }
  };

  // Função para buscar o estado pelo ID
  const fetchEstado = async (ufId: number) => {
    try {
      const response = await fetch(`${API_IBGE.estados}`);
      if (!response.ok) {
        throw new Error("Falha ao carregar a lista de estados");
      }
      const data: Estado[] = await response.json();
      // Encontra o estado na lista que corresponde ao ufId do usuário
      const estadoEncontrado = data.find((est) => est.id === ufId);
      if (estadoEncontrado) {
        setEstado(estadoEncontrado);
      } else {
        setEstado(null); // Define como nulo se não encontrar
        console.warn(`Estado com ID ${ufId} não encontrado.`);
      }
    } catch (error) {
      console.error("Erro ao carregar o estado:", error);
      setEstado(null);
    }
  };

  // UseEffect para carregar as informações de cidade e estado ao carregar o componente
  useEffect(() => {
    fetchEstado(ufId); // Obtém o estado
  }, [ufId]);

  return (
    <>
      <Card sx={{ maxWidth: 345, margin: 2, display: "inline-block" }}>
        <CardContent
          sx={{
            position: "relative",
            paddingTop: "30px",
          }}
        >
          <Box
            sx={{
              display: "block",
              position: "absolute",
              top: "4px",
              right: "8px",
              backgroundColor: "#cfe9ff",
              color: "#001120",
              padding: "4px 8px",
              borderRadius: "4px",
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "12px",
            }}
          >
            {financialRegister}
          </Box>
          <Typography variant="h6" component="div">
            {name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {email}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {phone}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Data de Criação:</strong> {formatDate(createdAt)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Estado:</strong> {estado?.nome || "Carregando..."}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Cidade:</strong> {capitalizeWords(city?.name)}
          </Typography>
        </CardContent>
        {role !== "PARTNER" && (
          <CardActions>
            <Button
              size="small"
              color="error"
              onClick={handleOpenDialog}
              disabled={isDeleting} // Desabilita o botão enquanto exclui
            >
              Excluir
            </Button>
          </CardActions>
        )}
      </Card>

      {/* Caixa de confirmação de exclusão */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          {"Tem certeza que deseja excluir este usuário?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Você está prestes a excluir o usuário <strong>{name}</strong> de
            forma permanente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            color="primary"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            color="secondary"
            variant="contained"
            autoFocus
            disabled={isDeleting}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de notificação */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GerenciarUsuariosCardUser;
